// =============================================================================
// components/community/ChannelTiles.tsx — the two channel affordances of the
// reference community interior (IMG_5877), recolored to the current lavender
// system:
//   · PinnedChannelTile — square glass tile (icon bubble + unread badge + name)
//     for the featured/pinned row (General / Announcements / Random).
//   · ChannelGridTile — rounded tile for the 2-column "All Channels" grids,
//     with a per-channel accent tint, optional "New!" pill and unread badge.
// Shared: channelTypeIcon() + accentFor() so every channel keeps a stable icon
// and tint across the pinned row and the grids.
// =============================================================================
import React from 'react';
import { View, Text, StyleSheet } from 'react-native';
import { Ionicons, IoniconName } from '../ui/Icon';
import GlassSurface from '../glass/GlassSurface';
import { glassTokens } from '../glass/tokens';
import { colors, spacing, fonts } from '../../constants/theme';

// Minimal shape both tiles render. `unread`/`isNew`/`liveCount` come from the
// grouped-channels endpoint when it answers; the client-side fallback leaves
// them 0/false (no fake badges).
export interface ChannelTileData {
  id: string;
  name: string;
  type?: string;
  unread?: number;
  isNew?: boolean;
  liveCount?: number;
}

// Every section/type variant the reference shows (events / FAQ / giveaways /
// live / lounge / support / announcements / dev / stage / general…) gets a
// sensible icon; anything unknown degrades to the chat glyph — a community with
// any channel mix renders gracefully.
export function channelTypeIcon(type?: string, name?: string): IoniconName {
  const t = (type || '').toUpperCase();
  const n = (name || '').toLowerCase();
  if (t.includes('VOICE') || t.includes('STAGE') || n === 'live' || n.includes('live ') || n.includes('voice') || n.includes('stage')) return 'volume-medium';
  if (t.includes('ANNOUNCE') || n.startsWith('announce') || n.includes('news')) return 'megaphone';
  if (t.includes('FORUM') || t.includes('THREAD')) return 'chatbubbles';
  if (t.includes('EVENT') || n.includes('event')) return 'calendar';
  if (n.includes('faq')) return 'help-circle';
  if (n.includes('support') || n.includes('help') || n.includes('ticket')) return 'headset';
  if (n.includes('giveaway') || n.includes('airdrop')) return 'gift';
  if (n.includes('lounge') || n.includes('random') || n.includes('off-topic') || n.includes('offtopic')) return 'dice';
  if (n.includes('dev') || n.includes('code') || n.includes('engineering')) return 'code-slash';
  if (n.includes('welcome') || n.includes('intro')) return 'hand-left';
  if (n.includes('rule')) return 'shield-checkmark';
  if (n.includes('trade') || n.includes('trading') || n.includes('market') || n.includes('price')) return 'trending-up';
  return 'chatbox-ellipses';
}

// Per-channel accent tints — the reference gives each channel its own soft hue
// (Live = purple, Lounge = pink, Announcements = pink/red, Events = blue…).
// Known channel kinds get their SEMANTIC accent so the interior reads like the
// reference; everything else falls back to a stable hash within the palette.
const ACCENTS = ['#6C5CE7', '#A855F7', '#DB2777', '#2563EB', '#0D9488', '#F59E0B'];
const SEMANTIC_ACCENTS: [RegExp, string][] = [
  [/live|voice|stage/, '#6C5CE7'],        // purple (reference "Live")
  [/lounge|random|off.?topic/, '#DB2777'], // pink (reference "Lounge")
  [/announce|news/, '#EC4899'],            // pink/magenta megaphone
  [/event/, '#2563EB'],                    // blue
  [/support|help|ticket|faq/, '#0D9488'],  // teal
  [/giveaway|airdrop/, '#6C5CE7'],         // purple (reference "Giveaways")
  [/dev|code|engineering/, '#7C3AED'],     // violet
  [/trade|trading|market|price/, '#F59E0B'], // amber
];
export function accentFor(key: string): string {
  const k = String(key || '').toLowerCase();
  for (const [re, color] of SEMANTIC_ACCENTS) {
    if (re.test(k)) return color;
  }
  let h = 0;
  for (let i = 0; i < k.length; i++) h = (h * 31 + k.charCodeAt(i)) >>> 0;
  return ACCENTS[h % ACCENTS.length];
}

function Badge({ count }: { count: number }) {
  if (!count || count <= 0) return null;
  return (
    <View style={styles.badge}>
      <Text style={styles.badgeText}>{count > 99 ? '99+' : count}</Text>
    </View>
  );
}

// "New!" pill — primary fill normally; on a FILLED (accent) tile it inverts to
// white with accent text, exactly like the reference's "Giveaways  New!" pill.
function NewPill({ onAccent = false, accent = colors.primary }: { onAccent?: boolean; accent?: string }) {
  return (
    <View style={[styles.newPill, onAccent && styles.newPillInverted]}>
      <Text style={[styles.newPillText, onAccent && { color: accent }]}>New!</Text>
    </View>
  );
}

/** Large square glass tile for the pinned/featured channels row (IMG_5877:
 * big icon top-left, unread badge top-right, active-dot + label bottom-left). */
export function PinnedChannelTile({ channel, onPress, onLongPress }: { channel: ChannelTileData; onPress: () => void; onLongPress?: () => void }) {
  const accent = accentFor(channel.name || channel.id);
  const unread = channel.unread ?? 0;
  return (
    <GlassSurface
      variant="card"
      radius={glassTokens.radius.lg}
      onPress={onPress}
      onLongPress={onLongPress}
      style={styles.pinnedTile}
      contentStyle={styles.pinnedContent}
      accessibilityRole="button"
      accessibilityLabel={`${channel.name} channel${unread > 0 ? `, ${unread} unread` : ''}`}
    >
      <View style={styles.pinnedTopRow}>
        <View style={[styles.pinnedIcon, { backgroundColor: accent + '1F' }]}>
          <Ionicons name={channelTypeIcon(channel.type, channel.name)} size={24} color={accent} />
        </View>
        {unread > 0 && <Badge count={unread} />}
      </View>
      <View style={styles.pinnedNameRow}>
        {unread > 0 && <View style={[styles.activeDot, { backgroundColor: accent }]} />}
        <Text style={styles.pinnedName} numberOfLines={1}>{channel.name}</Text>
      </View>
    </GlassSurface>
  );
}

/** Tall stadium pill for the 2-column channel grids (IMG_5877). Variants match
 * the reference and are driven by REAL data only:
 *   · isNew      → solid accent fill, white text, white "New!" pill
 *   · liveCount  → soft tint + live dot-count pill
 *   · unread     → stronger tint + count badge
 *   · default    → quiet accent tint                                        */
export function ChannelGridTile({ channel, onPress, onLongPress }: { channel: ChannelTileData; onPress: () => void; onLongPress?: () => void }) {
  const accent = accentFor(channel.name || channel.id);
  const unread = channel.unread ?? 0;
  const live = channel.liveCount ?? 0;
  const filled = !!channel.isNew;
  const fg = filled ? '#FFFFFF' : accent;
  return (
    <GlassSurface
      variant="pill"
      radius={glassTokens.radius.pill}
      onPress={onPress}
      onLongPress={onLongPress}
      surfaceColor={filled ? accent : accent + (unread > 0 || live > 0 ? '22' : '12')}
      borderColor={filled ? accent : accent + '26'}
      sheen={false}
      shadow={filled}
      style={styles.gridTile}
      contentStyle={styles.gridContent}
      accessibilityRole="button"
      accessibilityLabel={`${channel.name} channel${unread > 0 ? `, ${unread} unread` : ''}${channel.isNew ? ', new' : ''}`}
    >
      <Ionicons name={channelTypeIcon(channel.type, channel.name)} size={15} color={fg} />
      <Text style={[styles.gridName, filled && styles.gridNameFilled]} numberOfLines={1}>{channel.name}</Text>
      {channel.isNew ? <NewPill onAccent={filled} accent={accent} /> : null}
      {live > 0 ? (
        <View style={[styles.livePill]}>
          <View style={styles.liveDot} />
          <Text style={styles.livePillText}>{live > 99 ? '99+' : live}</Text>
        </View>
      ) : (
        <Badge count={unread} />
      )}
    </GlassSurface>
  );
}

const styles = StyleSheet.create({
  // Pinned tiles — big soft squares (reference IMG_5877: icon block top-left,
  // unread badge top-right, dot + label pinned to the bottom-left).
  pinnedTile: { flex: 1 },
  pinnedContent: {
    justifyContent: 'space-between',
    paddingVertical: spacing.md,
    paddingHorizontal: spacing.md,
    minHeight: 104,
  },
  pinnedTopRow: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
  },
  pinnedIcon: {
    width: 46, height: 46, borderRadius: 14,
    alignItems: 'center', justifyContent: 'center',
  },
  pinnedNameRow: { flexDirection: 'row', alignItems: 'center', gap: 5, maxWidth: '100%', marginTop: spacing.sm },
  activeDot: { width: 6, height: 6, borderRadius: 3 },
  pinnedName: { ...fonts.semibold, fontSize: 13, color: colors.ink, flexShrink: 1 },
  // Grid tiles — tall stadium pills, consistent height (reference IMG_5877).
  gridTile: { width: '48%' },
  gridContent: {
    flexDirection: 'row', alignItems: 'center', gap: spacing.sm,
    paddingHorizontal: spacing.lg, minHeight: 52,
  },
  gridName: { ...fonts.semibold, fontSize: 14, color: colors.ink, flex: 1 },
  gridNameFilled: { color: '#FFFFFF' },
  // Badges
  badge: {
    minWidth: 20, height: 20, borderRadius: 10, paddingHorizontal: 5,
    backgroundColor: '#F7931A',
    alignItems: 'center', justifyContent: 'center',
  },
  badgeText: { ...fonts.bold, fontSize: 11, color: '#FFFFFF' },
  newPill: {
    borderRadius: 999, paddingHorizontal: 8, paddingVertical: 2,
    backgroundColor: colors.primary,
  },
  newPillInverted: { backgroundColor: '#FFFFFF' },
  newPillText: { ...fonts.bold, fontSize: 10, color: '#FFFFFF' },
  livePill: {
    flexDirection: 'row', alignItems: 'center', gap: 4,
    borderRadius: 999, paddingHorizontal: 7, paddingVertical: 2,
    backgroundColor: colors.error + '18',
  },
  liveDot: { width: 6, height: 6, borderRadius: 3, backgroundColor: colors.error },
  livePillText: { ...fonts.bold, fontSize: 10, color: colors.error },
});
